import React, { useState, useEffect, useContext, useCallback } from 'react';

// Material-UI Imports
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

// Custom Component Imports
import ArgonBox from 'components/ArgonBox';
import CustomInput from 'components/CustomInput';
import ArgonButton from 'components/ArgonButton';
import ArgonSelect from 'components/ArgonSelect';
import ArgonTypography from 'components/ArgonTypography';

// Context and Service Imports
import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';
import { saveOrgDetails } from 'services/org';
import { RecentContext } from 'context/recentContext';

const protocol = process.env.REACT_APP_HTTP;
const serviceUrl = process.env.REACT_APP_SERVICE_URL;

// --- Constants defined outside the component for performance ---

const API_BASE_URL = `${protocol}://${serviceUrl}`;

if (!protocol || !serviceUrl) {
  console.error('FATAL ERROR: REACT_APP_HTTP and/or REACT_APP_SERVICE_URL are not defined. Check your .env files.');
}

const OAUTH_AUTH_URL = `${API_BASE_URL}/api/oauth2/auth`;
const SALESFORCE_AUTH_EVENT_TYPE = 'salesforce-auth';

const TYPE_OPTIONS = [
  { label: 'Developer', value: 'Developer' },
  { label: 'Production', value: 'Production' },
  { label: 'Testing', value: 'Testing' },
];

const PLATFORM_TYPES = [
  { value: 'CPQ', label: 'CPQ' },
  { value: 'RC', label: 'Revenue Cloud' },
  { value: 'OTHER', label: 'Other' },
];

const INITIAL_ORG_STATE = {
  name: '',
  org_id: '',
  org_type: '',
  type: '',
  client_id: '',
  client_secret: '',
  instance_url: '',
  configuration: {},
  salesforce_access_token: '',
  salesforce_refresh_token: '',
  salesforce_env: 'Production',
};

function OrgSetup({ orgData, onClose, refreshOrgs }) {
  const { addRecentOrg } = useContext(RecentContext);
  // --- State Management ---
  const [org, setOrg] = useState(INITIAL_ORG_STATE);
  const [errors, setErrors] = useState({});
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected'
  const [isEnvDialogOpen, setIsEnvDialogOpen] = useState(false);
  const [popupWindow, setPopupWindow] = useState(null);

  // --- Contexts and Hooks ---
  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();

  // --- Effects ---
  useEffect(() => {
    if (orgData) {
      const initialOrg = {
        ...INITIAL_ORG_STATE,
        ...orgData,
      };
      setOrg(initialOrg);
      if (initialOrg.salesforce_access_token && initialOrg.salesforce_refresh_token) {
        setConnectionStatus('connected');
      }
    } else {
      setOrg(INITIAL_ORG_STATE);
      setConnectionStatus('disconnected');
    }
  }, [orgData]);

  // Effect to handle the message from the OAuth popup window
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: Ensure the message comes from our own origin.
      // The backend now dynamically sets the target origin, so we just need
      // to verify it matches our window's origin.
      // --- THIS IS THE FIX ---
      const apiOrigin = new URL(API_BASE_URL).origin;
      if (event.origin !== apiOrigin) {
        console.warn(`Message received from unexpected origin: ${event.origin}. Expected: ${apiOrigin}`);
        return;
      }

      if (event.data.type === SALESFORCE_AUTH_EVENT_TYPE) {
        if (popupWindow) {
          popupWindow.close();
          setPopupWindow(null);
        }

        if (event.data.status === 'success' && event.data.org) {
          setOrg((prevOrg) => ({ ...prevOrg, ...event.data.org }));
          setConnectionStatus('connected');
          addNotification({
            title: ['Salesforce Connected'],
            details: 'Successfully authenticated and connected to Salesforce.',
            severity: 'success',
          });
        } else {
          setConnectionStatus('disconnected');
          addNotification({
            title: ['Salesforce Auth Failed'],
            details: event.data.errorDescription || 'Could not authenticate. Please check credentials or try again.',
            severity: 'error',
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [popupWindow, addNotification]);

  // --- Event Handlers (memoized with useCallback) ---
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    if (name.startsWith('configuration.')) {
      const configKey = name.split('.')[1];
      setOrg((prevOrg) => ({
        ...prevOrg,
        configuration: { ...prevOrg.configuration, [configKey]: value },
      }));
    } else {
      setOrg((prevOrg) => ({ ...prevOrg, [name]: value }));
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  }, []);

  const initiateSalesforceAuth = useCallback(
    (env) => {
      setIsEnvDialogOpen(false);

      // --- Validation ---
      const newErrors = {};
      if (!org.client_id) newErrors.client_id = 'Client ID is required.';
      if (!org.client_secret) newErrors.client_secret = 'Client Secret is required.';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        addNotification({
          title: ['Validation Error'],
          details: 'Client ID and Client Secret are required to connect.',
          severity: 'error',
        });
        return;
      }

      setErrors({});
      setConnectionStatus('connecting');
      addNotification({
        title: ['Connecting to Salesforce'],
        details: 'Please follow instructions in the popup window.',
        severity: 'info',
      });

      // We still update the state for persistence, but we don't rely on it for the URL
      setOrg((prevOrg) => ({ ...prevOrg, salesforce_env: env }));

      // ==================================================================
      // THE FIX IS HERE: Use the `env` parameter directly
      // ==================================================================

      console.log('ENV', env);
      const salesforceLoginUrl = env === 'Sandbox' ? 'https://test.salesforce.com' : 'https://login.salesforce.com';

      const params = new URLSearchParams({
        client_id: org.client_id,
        client_secret: org.client_secret,
        login_url: salesforceLoginUrl,
      });

      // +++ THIS IS THE FIX +++
      // Explicitly add the frontend's origin to the request.
      // window.location.origin is dynamic and works in any environment.
      params.append('client_origin', window.location.origin);
      // ++++++++++++++++++++++++

      const authUrl = `${OAUTH_AUTH_URL}?${params.toString()}`;

      const newWindow = window.open(authUrl, 'SalesforceLogin', 'width=600,height=600');
      setPopupWindow(newWindow);

      // Monitor if the user closes the popup manually
      const checkWindowClosed = setInterval(() => {
        if (newWindow && newWindow.closed) {
          setConnectionStatus((prevStatus) => {
            if (prevStatus === 'connecting') {
              addNotification({
                title: ['Connection Attempt Ended'],
                details: 'Salesforce connection window was closed before completion.',
                severity: 'warning',
              });
              return 'disconnected';
            }
            return prevStatus;
          });
          clearInterval(checkWindowClosed);
          setPopupWindow(null);
        }
      }, 1000);
    },
    [org.client_id, org.client_secret, addNotification]
  ); // Dependencies are correct for this version

  const handleConnectClick = useCallback(() => {
    setIsEnvDialogOpen(true);
  }, []);

  const handleDisconnect = useCallback(() => {
    setOrg((prevOrg) => ({
      ...prevOrg,
      salesforce_access_token: '',
      salesforce_refresh_token: '',
      instance_url: '',
      org_id: '',
    }));
    setConnectionStatus('disconnected');
    addNotification({
      title: ['Salesforce Disconnected'],
      details: 'You have been disconnected. Save changes to persist this.',
      severity: 'info',
    });
  }, [addNotification]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const newErrors = {};
      if (!org.name.trim()) newErrors.name = 'Name is required';
      if (!org.type.trim()) newErrors.type = 'Platform is required';
      if (!org.org_type.trim()) newErrors.org_type = 'Org Type is required';
      if (!org.client_id.trim()) newErrors.client_id = 'Client ID is required';
      if (!org.client_secret.trim()) newErrors.client_secret = 'Client Secret is required';
      if (connectionStatus !== 'connected' && !orgData?.salesforce_access_token) {
        newErrors.connection = 'Salesforce connection is required before saving.';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        addNotification({
          title: ['Validation Error'],
          details: `Please correct the following: ${Object.values(newErrors).join(' ')}`,
          severity: 'error',
        });
        return;
      }

      addNotification({
        title: ['Saving Organization', org.name],
        details: 'Attempting to save organization details...',
        severity: 'info',
      });

      try {
        const dataToSend = { ...org };
        if (dataToSend._id) delete dataToSend._id;

        const savedOrgResponse = await saveOrgDetails(dataToSend);
        if (savedOrgResponse.error) {
          throw new Error(savedOrgResponse.error);
        }
        // Add to recent orgs (prefer response org data if available)
        const savedOrg = savedOrgResponse.org || dataToSend;
        addRecentOrg(savedOrg);
        addNotification({
          title: ['Organization Saved', org.name],
          details: 'Organization details were successfully saved.',
          severity: 'success',
        });
        onClose();
        refreshOrgs();
      } catch (error) {
        console.error('Error saving org details:', error);
        addNotification({
          title: ['Save Failed', org.name],
          details: error.message || 'Could not save organization details. Please try again.',
          severity: 'error',
        });
      }
    },
    [org, orgData, connectionStatus, userId, addNotification, onClose, refreshOrgs]
  );

  // --- Render Logic ---
  const isConnected = connectionStatus === 'connected';
  const isConnecting = connectionStatus === 'connecting';

  return (
    <ArgonBox p={3} style={{ backgroundColor: '#f8f9fa' }}>
      <form onSubmit={handleSubmit}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5">{orgData ? 'Edit Org Configuration' : 'Add New Org'}</Typography>
          <Box>
            <ArgonButton type="submit" variant="contained" color="primary">
              {orgData ? 'Save Changes' : 'Add Org'}
            </ArgonButton>
            <ArgonButton variant="outlined" color="secondary" onClick={onClose} sx={{ ml: 2 }}>
              Cancel
            </ArgonButton>
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* --- Basic Org Info --- */}
              <Grid item xs={12}>
                <CustomInput
                  label="Name"
                  name="name"
                  value={org.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ArgonSelect
                  name="type"
                  placeholder="Select Platform"
                  value={org.type}
                  onChange={handleChange}
                  options={PLATFORM_TYPES}
                  error={!!errors.type}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ArgonSelect
                  name="org_type"
                  placeholder="Select Org Type"
                  value={org.org_type}
                  onChange={handleChange}
                  options={TYPE_OPTIONS}
                  error={!!errors.org_type}
                />
              </Grid>

              {/* --- Connection Credentials --- */}
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Client ID"
                  name="client_id"
                  value={org.client_id}
                  onChange={handleChange}
                  error={!!errors.client_id}
                  helperText={errors.client_id}
                  type="password"
                  disabled={isConnected}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Client Secret"
                  name="client_secret"
                  value={org.client_secret}
                  onChange={handleChange}
                  error={!!errors.client_secret}
                  helperText={errors.client_secret}
                  type="password"
                  disabled={isConnected}
                  fullWidth
                />
              </Grid>

              {/* --- Salesforce Data (Read-only) --- */}
              <Grid item xs={12} sm={6}>
                <CustomInput readOnly label="Org ID (from Salesforce)" name="org_id" value={org.org_id} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Instance URL (from Salesforce)"
                  name="instance_url"
                  value={org.instance_url}
                  readOnly
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Access Token"
                  name="salesforce_access_token"
                  value={isConnected ? '••••••••••' : ''}
                  readOnly
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Refresh Token"
                  name="salesforce_refresh_token"
                  value={isConnected ? '••••••••••' : ''}
                  readOnly
                  fullWidth
                />
              </Grid>

              {/* --- Action Button --- */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                {isConnected ? (
                  <Button variant="contained" color="error" onClick={handleDisconnect}>
                    Disconnect from Salesforce
                  </Button>
                ) : (
                  <Button variant="contained" color="info" onClick={handleConnectClick} disabled={isConnecting}>
                    {isConnecting ? 'Connecting...' : 'Connect to Salesforce'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>

      {/* --- Environment Selection Dialog --- */}
      <Dialog open={isEnvDialogOpen} onClose={() => setIsEnvDialogOpen(false)}>
        <DialogTitle>Select Salesforce Environment</DialogTitle>
        <DialogContent>
          <DialogContentText>Choose the type of Salesforce organization you want to connect to.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={() => setIsEnvDialogOpen(false)} variant="filled" color="secondary">
            Cancel
          </Button>

          <ArgonBox>
            <Button sx={{ mr: 2 }} onClick={() => initiateSalesforceAuth('Sandbox')} variant="contained" color="info">
              Sandbox / Test
            </Button>
            <Button onClick={() => initiateSalesforceAuth('Production')} variant="contained" color="info" autoFocus>
              Production
            </Button>
          </ArgonBox>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

export default OrgSetup;
